// src/utils/subdomainUtils.ts
// Enhanced version with cross-account isolation fixes, Firebase-safe clearing, and multi-domain support
// ✅ Supports both production (cluva.io) and staging (cluva.in) environments
// ✅ FIXED: Prevents unnecessary redirects and back button issues

import { API_URLS, getAccessToken } from '../api/urls';

interface SubdomainInfo {
  subdomain: string | null;
  isSubdomain: boolean;
  fullDomain: string;
  baseDomain: string;
}

// ✅ Helper to safely clear sessionStorage without breaking Firebase OAuth redirect
const clearSessionStorageSafely = () => {
  try {
    const firebaseKeys: string[] = [];
    
    // Find all Firebase keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('firebase:')) {
        firebaseKeys.push(key);
      }
    }
    
    // Store Firebase values temporarily
    const firebaseData = firebaseKeys.map(key => ({
      key,
      value: sessionStorage.getItem(key)
    }));
    
    // Clear everything
    sessionStorage.clear();
    
    // Restore Firebase keys
    firebaseData.forEach(({ key, value }) => {
      if (value !== null) {
        sessionStorage.setItem(key, value);
      }
    });
    
    console.log('✅ SessionStorage cleared safely (preserved Firebase keys)');
  } catch (error) {
    console.warn('⚠️ Error clearing sessionStorage safely:', error);
    // Fallback to normal clear if something goes wrong
    sessionStorage.clear();
  }
};

export class SubdomainManager {
  private static APP_SUBDOMAIN = "app";
  private static BLOCKER_KEY = 'nav_blocker_active';
  private static SUBDOMAIN_KEY = 'workspace_subdomain';

  /**
   * ✅ NEW: Determine the base domain based on current environment
   * @returns 'cluva.io' for production or 'cluva.in' for staging
   */
  static getBaseDomain(): string {
    const hostname = window.location.hostname;

    // Staging environment
    if (hostname.includes('cluva.in')) {
      return 'cluva.in';
    }

    // Production (default) — also returned for localhost so API calls use the correct domain
    return 'cluva.io';
  }

  /**
   * ✅ NEW: Get the app domain for the current environment
   * @returns 'app.cluva.io' or 'app.cluva.in'
   */
  private static getAppDomain(): string {
    const baseDomain = this.getBaseDomain();
    return `${this.APP_SUBDOMAIN}.${baseDomain}`;
  }

  /**
   * ✅ NEW: Get environment name for logging/debugging
   */
  static getEnvironmentName(): string {
    if (this.isLocalDevelopment()) {
      return 'local';
    }

    const baseDomain = this.getBaseDomain();
    return baseDomain === 'cluva.in' ? 'staging' : 'production';
  }

  static isLocalDevelopment(): boolean {
    const hostname = window.location.hostname;
    return hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
  }

  static getCurrentSubdomainInfo(): SubdomainInfo {
    const hostname = window.location.hostname;

    if (this.isLocalDevelopment()) {
      const storedSubdomain = this.getStoredWorkspaceSubdomain();
      return {
        subdomain: storedSubdomain,
        isSubdomain: !!storedSubdomain,
        fullDomain: hostname,
        baseDomain: hostname,
      };
    }

    const parts = hostname.split(".");
    if (parts.length < 3) {
      return {
        subdomain: null,
        isSubdomain: false,
        fullDomain: hostname,
        baseDomain: hostname,
      };
    }

    const subdomain = parts.slice(0, -2).join(".");
    const baseDomain = parts.slice(-2).join(".");

    return {
      subdomain: subdomain || null,
      isSubdomain: !!subdomain && subdomain !== this.APP_SUBDOMAIN,
      fullDomain: hostname,
      baseDomain: baseDomain,
    };
  }

  static getCurrentSubdomain(): string | null {
    const info = this.getCurrentSubdomainInfo();
    return info.subdomain;
  }

  static isOnWorkspaceSubdomain(): boolean {
    const info = this.getCurrentSubdomainInfo();
    return info.isSubdomain;
  }

  /**
   * ✅ UPDATED: Navigate to subdomain with multi-domain support
   * ✅ FIXED: Prevents unnecessary redirects and uses replace to fix back button
   * ✅ SECURE: Tokens transferred via server-issued one-time code — never in URL
   */
  static async navigateToSubdomain(
    subdomain: string,
    path: string = "/dashboard",
    transferTokens: boolean = true
  ): Promise<void> {
    const info = this.getCurrentSubdomainInfo();
    const protocol = window.location.protocol;
    const port = window.location.port ? `:${window.location.port}` : "";

    if (this.isLocalDevelopment()) {
      const targetPath = path.startsWith("/") ? path : `/${path}`;

      if (window.location.pathname !== targetPath) {
        window.location.href = `${protocol}//${info.baseDomain}${port}${targetPath}`;
      }
      return;
    }

    // ✅ Clean subdomain - works for both cluva.io and cluva.in
    const baseDomain = this.getBaseDomain();
    const cleanSubdomain = subdomain
      .replace(/\.cluva\.io$/, "")
      .replace(/\.cluva\.in$/, "")
      .toLowerCase();

    const cleanPath = path.startsWith("/") ? path : `/${path}`;

    // ✅ FIX: Check if already on target subdomain and path
    const currentSubdomain = this.getCurrentSubdomain();
    if (currentSubdomain === cleanSubdomain && window.location.pathname === cleanPath) {
      console.log("✅ Already on target subdomain and path, skipping redirect");
      return;
    }

    let queryParams = "";

    if (transferTokens) {
      try {
        const accessToken = getAccessToken();

        if (accessToken) {
          const response = await fetch(API_URLS.REQUEST_TRANSFER_CODE, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.code) {
              const params = new URLSearchParams({ code: data.code });

              const blockerActive = sessionStorage.getItem(this.BLOCKER_KEY);
              if (blockerActive === "true") params.set("nav_blocker", "true");

              if (localStorage.getItem("showBetaPopupOnce")) {
                params.set("show_beta_popup", "1");
                localStorage.removeItem("showBetaPopupOnce");
              }

              queryParams = `?${params.toString()}`;
            }
          } else {
            console.error("❌ Failed to request transfer code:", response.status);
          }
        }
      } catch (error) {
        console.error("❌ Failed to request transfer code:", error);
      }
    }

    // ✅ Use environment-aware base domain — only a short-lived code in URL, never real tokens
    const newUrl = `${protocol}//${cleanSubdomain}.${baseDomain}${port}${cleanPath}${queryParams}`;
    console.log(`🚀 Navigating to ${this.getEnvironmentName()}:`, `${protocol}//${cleanSubdomain}.${baseDomain}${port}${cleanPath}`);

    // ✅ FIX: Use window.location.replace to prevent back button issues
    window.location.replace(newUrl);
  }

  /**
   * ✅ UPDATED: Navigate to app domain with multi-domain support
   */
  static navigateToAppDomain(path: string = "/login", clearTokens: boolean = false) {
    const info = this.getCurrentSubdomainInfo();
    const protocol = window.location.protocol;
    const port = window.location.port ? `:${window.location.port}` : "";

    // ✅ CRITICAL: Clear subdomain storage FIRST
    this.clearWorkspaceSubdomain();

    if (clearTokens) {
      console.log("🧹 Clearing all tokens and session data");
      
      // Clear session storage (Firebase-safe)
      clearSessionStorageSafely();
      
      // Dispatch logout event
      window.dispatchEvent(new Event('user_logout'));
      
      // Clear all auth-related data
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("current_user_id");
      
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (
          key.includes("workspace_id_user_") ||
          key.includes("user_profile_user_") ||
          key.includes("workspace_data_user_") ||
          key.includes("workspace_icon_user_")
        ) {
          localStorage.removeItem(key);
        }
      });
      
      delete (window as any).__feebdack_tokens;
    }

    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    let appDomainUrl;
    
    if (this.isLocalDevelopment()) {
      appDomainUrl = `${protocol}//${info.baseDomain}${port}${cleanPath}`;
    } else {
      // ✅ Use environment-aware app domain
      const appDomain = this.getAppDomain();
      appDomainUrl = `${protocol}//${appDomain}${port}${cleanPath}`;
    }
    
    if (clearTokens) {
      appDomainUrl += (cleanPath.includes('?') ? '&' : '?') + 'logout=true&_t=' + Date.now();
    }

    console.log(`🔄 Redirecting to ${this.getEnvironmentName()} app domain:`, appDomainUrl);
    
    // ✅ Use replace to prevent back button issues
    window.location.replace(appDomainUrl);
  }

  /**
   * ✅ Token restoration with proper logout handling and verification
   * ✅ SECURE: Reads a one-time code from ?code= URL param, exchanges it with
   *    the backend for real tokens, then immediately removes the code from the URL.
   */
  static async restoreTokensFromUrl(): Promise<boolean> {
    try {
      // logout flag is a query param set by navigateToAppDomain — keep reading from search
      const searchParams = new URLSearchParams(window.location.search);
      const isLogout = searchParams.get('logout') === 'true';

      if (isLogout) {
        console.log('🚫 Logout parameter detected - clearing all data');

        this.clearWorkspaceSubdomain();
        clearSessionStorageSafely();

        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('current_user_id');

        delete (window as any).__feebdack_tokens;

        window.history.replaceState({}, document.title, window.location.pathname);

        return false;
      }

      const logoutInProgress = sessionStorage.getItem('logout_in_progress');
      if (logoutInProgress === 'true') {
        console.log('🚫 Logout in progress');

        this.clearWorkspaceSubdomain();
        clearSessionStorageSafely();

        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('current_user_id');

        delete (window as any).__feebdack_tokens;

        window.history.replaceState({}, document.title, window.location.pathname);

        return false;
      }

      const code = searchParams.get('code');
      const navBlocker = searchParams.get('nav_blocker');
      const showBetaPopup = searchParams.get('show_beta_popup');

      if (navBlocker === 'true') {
        sessionStorage.setItem(this.BLOCKER_KEY, 'true');
      }
      if (showBetaPopup === '1') {
        localStorage.setItem('showBetaPopupOnce', 'true');
      }

      let restored = false;

      if (code) {
        // Remove code from URL immediately — it's single-use and should not linger
        const cleanParams = new URLSearchParams(searchParams);
        cleanParams.delete('code');
        cleanParams.delete('nav_blocker');
        cleanParams.delete('show_beta_popup');
        const newSearch = cleanParams.toString() ? `?${cleanParams.toString()}` : '';
        window.history.replaceState({}, document.title, window.location.pathname + newSearch);

        try {
          // The transfer code is the credential — use it as the Bearer token.
          // On the destination subdomain, localStorage is empty (different origin),
          // so the code itself is the only valid auth for this exchange request.
          const response = await fetch(API_URLS.VERIFY_TRANSFER_CODE, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${code}`,
            },
            body: JSON.stringify({ code }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.access_token && data.refresh_token) {
              localStorage.setItem('access_token', data.access_token);
              localStorage.setItem('refresh_token', data.refresh_token);

              try {
                const parts = data.access_token.split('.');
                if (parts.length === 3) {
                  const payload = parts[1] + '='.repeat((4 - (parts[1].length % 4)) % 4);
                  const decoded = JSON.parse(atob(payload));
                  if (decoded.sub) {
                    const userId = parseInt(decoded.sub);
                    if (!isNaN(userId)) {
                      localStorage.setItem('current_user_id', userId.toString());
                    }
                  }
                }
              } catch (e) {
                console.warn('⚠️ Could not decode token payload');
              }

              console.log('✅ Tokens restored via transfer code exchange');
              restored = true;

              // Store the current subdomain so WorkspaceSubdomainGuard doesn't
              // treat the first hard-refresh after token restoration as a mismatch.
              const currentSub = this.getCurrentSubdomain();
              if (currentSub && this.isOnWorkspaceSubdomain()) {
                localStorage.setItem(this.SUBDOMAIN_KEY, currentSub);
                console.log('✅ Subdomain stored after token restoration:', currentSub);
              }
            }
          } else {
            console.error('❌ Transfer code exchange failed:', response.status);
          }
        } catch (e) {
          console.error('❌ Transfer code exchange error:', e);
        }
      }

      return restored;
    } catch (error) {
      console.error("❌ Failed to restore tokens:", error);
      return false;
    }
  }

  /**
   * ✅ UPDATED: Store subdomain with user verification (multi-domain support)
   */
  static storeWorkspaceSubdomain(subdomain: string) {
    try {
      // ✅ Clean subdomain for both domains
      const cleanSubdomain = subdomain
        .replace(/\.cluva\.io$/, "")
        .replace(/\.cluva\.in$/, "")
        .toLowerCase();

      // ✅ Get current user ID to verify ownership
      const userId = localStorage.getItem("current_user_id");
      
      if (userId) {
        // Verify this subdomain belongs to this user
        const userProfileKey = `workspace_data_user_${userId}`;
        const storedData = localStorage.getItem(userProfileKey);
        
        if (storedData && storedData !== "null") {
          try {
            const workspaceData = JSON.parse(storedData);
            const expectedSubdomain = this.extractSubdomainFromUrl(workspaceData.subdomain || '');
            
            if (expectedSubdomain && expectedSubdomain !== cleanSubdomain) {
              console.error(`⚠️ Subdomain mismatch! Attempted to store ${cleanSubdomain} but user workspace is ${expectedSubdomain}`);
              console.error("This could be a cross-user contamination attempt!");
              
              // Store the correct subdomain instead
              localStorage.setItem(this.SUBDOMAIN_KEY, expectedSubdomain);
              console.log(`✅ Corrected subdomain stored: ${expectedSubdomain}`);
              return;
            }
          } catch (parseError) {
            console.warn("Failed to parse workspace data:", parseError);
          }
        }
      }
      
      localStorage.setItem(this.SUBDOMAIN_KEY, cleanSubdomain);
      console.log(`✅ Workspace subdomain stored (${this.getEnvironmentName()}):`, cleanSubdomain);
    } catch (error) {
      console.warn("Could not store workspace subdomain:", error);
    }
  }

  static getStoredWorkspaceSubdomain(): string | null {
    try {
      return localStorage.getItem(this.SUBDOMAIN_KEY);
    } catch (error) {
      return null;
    }
  }

  static isOnCorrectWorkspaceSubdomain(): boolean {
    const currentSubdomain = this.getCurrentSubdomain();
    const storedSubdomain = this.getStoredWorkspaceSubdomain();

    if (!currentSubdomain || !storedSubdomain) {
      return false;
    }

    return currentSubdomain === storedSubdomain;
  }

  static redirectToWorkspaceIfNeeded(path: string = "/dashboard"): boolean {
    if (this.isLocalDevelopment()) {
      return false;
    }

    const storedSubdomain = this.getStoredWorkspaceSubdomain();
    if (!storedSubdomain) {
      return false;
    }

    const currentSubdomain = this.getCurrentSubdomain();
    if (currentSubdomain !== storedSubdomain) {
      this.navigateToSubdomain(storedSubdomain, path);
      return true;
    }

    return false;
  }

  /**
   * ✅ Enhanced clearWorkspaceSubdomain with comprehensive cleanup
   */
  static clearWorkspaceSubdomain() {
    try {
      // Clear the main subdomain key
      localStorage.removeItem(this.SUBDOMAIN_KEY);
      
      // ✅ Also clear any legacy or backup subdomain keys
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (key.toLowerCase().includes('subdomain') || 
            key.toLowerCase().includes('workspace_subdomain')) {
          try {
            localStorage.removeItem(key);
            console.log(`🧹 Cleared subdomain key: ${key}`);
          } catch (err) {
            console.warn(`Failed to clear key ${key}:`, err);
          }
        }
      });
      
      // Clear from session storage as well (Firebase-safe)
      try {
        // Only clear specific subdomain keys from sessionStorage, not all
        sessionStorage.removeItem(this.SUBDOMAIN_KEY);
      } catch (err) {
        console.warn("Failed to clear subdomain from session storage:", err);
      }
      
      console.log("✅ All workspace subdomain data cleared");
    } catch (error) {
      console.warn("Could not clear workspace subdomain:", error);
    }
  }

  /**
   * ✅ Verify subdomain belongs to current user
   */
  static verifySubdomainOwnership(userId: number, subdomain: string): boolean {
    try {
      const key = `workspace_data_user_${userId}`;
      const storedData = localStorage.getItem(key);
      
      if (!storedData || storedData === "null") {
        console.warn("No workspace data found for verification");
        return false;
      }
      
      const workspaceData = JSON.parse(storedData);
      const extractedSubdomain = this.extractSubdomainFromUrl(workspaceData.subdomain || '');
      
      const matches = extractedSubdomain === subdomain;
      
      if (!matches) {
        console.warn(`⚠️ Subdomain mismatch! Expected: ${subdomain}, Found: ${extractedSubdomain}`);
        // Clear mismatched subdomain
        this.clearWorkspaceSubdomain();
      }
      
      return matches;
    } catch (error) {
      console.error("Error verifying subdomain ownership:", error);
      return false;
    }
  }

  /**
   * ✅ UPDATED: Extract subdomain from URL (multi-domain support)
   */
  static extractSubdomainFromUrl(url: string): string | null {
    try {
      // Remove both possible domain suffixes
      const cleanUrl = url
        .replace(/\.cluva\.io$/, "")
        .replace(/\.cluva\.in$/, "");

      const withoutProtocol = cleanUrl.replace(/^https?:\/\//, "");
      return withoutProtocol;
    } catch (error) {
      return null;
    }
  }

  static workspaceNameToSubdomain(name: string): string {
    return name.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  }

  /**
   * ✅ NEW: Build a full workspace URL with environment awareness
   * @param subdomain - The workspace subdomain
   * @param path - The path (default: '/dashboard')
   * @returns Full URL string
   */
  static buildWorkspaceUrl(subdomain: string, path: string = '/dashboard'): string {
    if (this.isLocalDevelopment()) {
      return `http://localhost:3000${path}`;
    }

    const cleanSubdomain = this.extractSubdomainFromUrl(subdomain);
    if (!cleanSubdomain) {
      throw new Error('Invalid subdomain provided');
    }

    const baseDomain = this.getBaseDomain();
    return `https://${cleanSubdomain}.${baseDomain}${path}`;
  }

  /**
   * ✅ NEW: Log current environment info for debugging
   */
  static logEnvironmentInfo(): void {
    console.log('🌐 Environment Info:', {
      environment: this.getEnvironmentName(),
      baseDomain: this.getBaseDomain(),
      appDomain: this.getAppDomain(),
      currentSubdomain: this.getCurrentSubdomain(),
      storedSubdomain: this.getStoredWorkspaceSubdomain(),
      isLocal: this.isLocalDevelopment(),
      hostname: window.location.hostname,
    });
  }
}

export const getCurrentSubdomain = () => SubdomainManager.getCurrentSubdomain();
export const isOnWorkspaceSubdomain = () => SubdomainManager.isOnWorkspaceSubdomain();
export const navigateToSubdomain = (subdomain: string, path?: string) =>
  SubdomainManager.navigateToSubdomain(subdomain, path);
export const navigateToAppDomain = (path?: string, clearTokens?: boolean) =>
  SubdomainManager.navigateToAppDomain(path, clearTokens);
export const storeWorkspaceSubdomain = (subdomain: string) =>
  SubdomainManager.storeWorkspaceSubdomain(subdomain);
export const getStoredWorkspaceSubdomain = () =>
  SubdomainManager.getStoredWorkspaceSubdomain();
export const redirectToWorkspaceIfNeeded = (path?: string) =>
  SubdomainManager.redirectToWorkspaceIfNeeded(path);
export const clearWorkspaceSubdomain = () =>
  SubdomainManager.clearWorkspaceSubdomain();
export const restoreTokensFromUrl = () =>
  SubdomainManager.restoreTokensFromUrl();